import { apiClient } from '../base-classes/apiBaseClass';
import { configure, makeAutoObservable, runInAction } from "mobx";
import notificationState from './notification-state';
import auth from './auth-state';

export interface IFilters {
  id: string,
}

interface Price {
  amount: string;
  currency_code: string;
}

interface BillingCycle {
  interval: string;
  frequency: number;
}

interface TrialPeriod {
  interval: string;
  frequency: number;
}

interface QuantityRange {
  minimum: number;
  maximum: number;
}

export interface ISubsPlan {
  id: string;
  product_id: string;
  type: string;
  description: string;
  name: string;
  billing_cycle: BillingCycle;
  trial_period: TrialPeriod;
  tax_mode: string;
  unit_price: Price;
  unit_price_overrides: any[];
  custom_data: any;
  status: string;
  access: JSON;
  quantity: QuantityRange;
  import_meta: any;
  created_at: string;
  updated_at: string;
  // Derived fields
  actualPrice: string;
  discountPrice: string | null;
  trialDays: number;
}

export interface IUserSubscription {
  id: number;
  package_name: string;
  userId: number;
  title: String;
  plan_id: string;
  transaction_id: string;
  subscription_id: string;
  currency_code: string;
  package_price: number;
  billing_cycle: string;
  paid_at: string;
  activationDate: string;
  expiryDate: string;
  canceled_at: string | null;
  createdAt: string; // Consider using Date type if working with Date objects
  updatedAt: string; // Consider using Date type if working with Date objects
  deleted: boolean;
  status: 'active' | 'inactive' | string; // Adjust according to possible statuses
  has_payment_method?: boolean; // Track if user has saved payment method
  payment_method_id?: string; // Paddle payment method ID
  is_free_plan?: boolean; // Track if current plan is free
  customer_id?: string; // Paddle customer ID from subscription
  address_id?: string; // Paddle address ID
  business_id?: string; // Paddle business ID
}


class SubscriptionState {
  _planList: ISubsPlan[] = [];
  _userSubscription!: IUserSubscription | null;
  errorMsg?: string;
  constructor() {
    // Changed from "never" to "always" for better state management discipline
    configure({ enforceActions: "always" });
    makeAutoObservable(this)
  }

  async filterSubscriptionPlans(filters: IFilters) {
  }
  get UserActiveSubscription(): ISubsPlan | null | undefined {
    if (!this._userSubscription) {
      return null;
    }
    const userSub = this._userSubscription;
    return this.planList.find(sb => sb.id === userSub.plan_id) || null;
  }
  get hasSubscription(): boolean {
    return !!this._userSubscription?.id;
  }
  get planList(): ISubsPlan[] {
    // if (this._planList.length === 0) {
    //   this.getSubscriptionPlans();
    // }
    return this._planList || [];
  }

  async subscriptionHistory(userId: string, config: { per_page?: number; page?: number }) {
    try {
      const response = await apiClient.get(`subscriptions-log-list?per_page=${config.per_page || 10}&page=${config.page}`);
      return response.data;
    } catch (e: any) {
      notificationState.addToasterError(this.errorMsg || 'Something went wrong, try again...');
    }
  }

  async adminUserSubscription(userId: string | number) {
    try {
      const response = await apiClient.get("admin/admin-user-subscription/" + userId);
      return response.data?.userSubscription || [];
    } catch (e: any) {
      notificationState.addToasterError(this.errorMsg || 'Something went wrong, try again...');
    }
  }
  async userSubscriptionHistory(userId: string | number, config: { per_page?: number; page?: number }) {
    try {
      const response = await apiClient.get(`admin/admin-user-subscription-log/${userId}?per_page=${config.per_page || 10}&page=${config.page}`);
      return response.data || {};
    } catch (e: any) {
      notificationState.addToasterError(this.errorMsg || 'Something went wrong, try again...');
    }
  }


  async getSubscriptionPlans(withActivePlan = false) {
    if (withActivePlan) {
      await this.getActiveSubscriptionPlan();
    }
    
    try {
      const response = await apiClient.get<any, any>("subscription-description-list");
      return (response as any).access || [];
    } catch (e) {
      notificationState.addToasterError("Something went wrong...");
    }
    return [];
  }
  async getSubscriptiondescriptionPlans(withActivePlan = false) {
    // if (withActivePlan) {
    //   await this.getActiveSubscriptionPlan();
    // }
    // this._planList = [];
    try {
      const response = await apiClient.get("subscription-description-list");
      // this._planList = response.json();
      return response;
    } catch (e) {
      notificationState.addToasterError("Something went wrong...");
    }
    return [];
  }
  async update(planId: string) {
    try {
      if (!this._userSubscription) {
        throw new Error("No active subscription found");
      }
      const response = await apiClient.post("update-subscription", {
        planId,
        subId: this._userSubscription.subscription_id,
      });
  
      if (response.status) {
        notificationState.addToasterSuccess(
          response.message || "Subscription updated successfully..."
        );
      } else {
        notificationState.addToasterError(
          response.message || "Something went wrong..."
        );
      }
  
      // Return the response object so the caller can access it
      return response;
    } catch (e: any) {
      const error = e.message || "Error...";
      notificationState.addToasterError(error);
  
      // Return the error to the caller
      return { status: false, message: error };
    } finally {
      await this.getSubscriptionPlans();
    }
  }
  

  async cancelUserSubscription(data: { userId: number | string; [key: string]: any }) {
    try {
      const response = await apiClient.post("admin/user-cancel-subscription", data);
      if (response.status) {
        notificationState.addToasterSuccess(response.message || 'Subscription cancelled successfully...');
      } else {
        notificationState.addToasterError(response.message || "Something went wrong...");
      }
      return response.status;
    } catch (e: any) {
      const error = e.message || "Error..."
      notificationState.addToasterError(error);
    }
  }

  async cancel() {
    try {
      if (!this._userSubscription) {
        throw new Error("No active subscription found");
      }
      const response = await apiClient.post("cancel-subscription", {
        subId: this._userSubscription.subscription_id,
        plan_id: this._userSubscription.plan_id,
        transaction_id: this._userSubscription.transaction_id
      });
      if (response.status) {
        runInAction(() => {
          this._userSubscription = null;
        });
        notificationState.addToasterSuccess(response.message || 'Subscription cancelled successfully...');
      } else {
        notificationState.addToasterError(response.message || "Something went wrong...");
      }
    } catch (e: any) {
      const error = e.message || "Error..."
      notificationState.addToasterError(error);
    }
    await this.getSubscriptionPlans();
  }

  async getActiveSubscriptionPlan() {
    try {
      const response = await apiClient.get<any, any>("current-package");
      this._userSubscription = response.data;
      
      // Update customer_id in auth state if returned from backend
      const responseData = response as any;
      if (responseData.user && responseData.user.customer_id && auth.currentUser) {
        runInAction(() => {
          if (auth.currentUser) {
            auth.currentUser.customer_id = responseData.user.customer_id;
            auth.currentUser.address_id = responseData.user.address_id;
            auth.currentUser.business_id = responseData.user.business_id;
          }
        });
        auth.synchronizeUserState();
        console.log('Customer ID updated from subscription data:', responseData.user.customer_id);
      }
      
      return this._userSubscription;
    } catch (e) {
      // notificationState.addToasterError("Something went wrong...");
    }
  }
 
async activateFreePlan(planId: string) {
  try {
    const userId = auth.currentUser?.id;
    if (!userId) throw new Error("User not authenticated");

    const response = await apiClient.post("activate-free-plan", {
      planId,
      userId
    });

    if (response.status) {
      notificationState.addToasterSuccess("Free plan activated successfully!");
      
      // Update local state
      await this.getActiveSubscriptionPlan();
      return response;
    } else {
      notificationState.addToasterError(response.message || "Activation failed");
      return null;
    }
  } catch (error) {
    const errorMessage = (error as Error).message || "Activation error";
    notificationState.addToasterError(errorMessage);
    return null;
  }
}
  

  async updateSubscriptionFeatures(planId: string, updatedFeatures: any) {
    try {
      const response = await apiClient.put(`subscription-description-list?plan_id=${planId}`, {
        access: updatedFeatures,
      });
      
        notificationState.addToasterSuccess(
          response.message || "Subscription features updated successfully..."
        );
     
      return response;
    } catch (e: any) {
      const error = e.message || "Error...";
      notificationState.addToasterError(error);
      return { status: false, message: error };
    }
  }

  // Check if user has payment method saved
  get hasPaymentMethod(): boolean {
    return this._userSubscription?.has_payment_method || false;
  }

  // Check if current plan is free
  get isCurrentPlanFree(): boolean {
    return this._userSubscription?.is_free_plan || this._userSubscription?.package_price === 0;
  }

  // Update payment method information
  async updatePaymentMethod(paymentMethodData: any) {
    if (!this._userSubscription) {
      notificationState.addToasterError("No active subscription found");
      return { status: false, message: "No active subscription found" };
    }
    
    try {
      const response = await apiClient.post("update-payment-method", {
        subscription_id: this._userSubscription.subscription_id,
        payment_method_id: paymentMethodData.payment_method_id,
        customer_id: paymentMethodData.customer_id
      });

      if (response.status) {
        notificationState.addToasterSuccess("Payment method updated successfully!");
        // Update local state
        runInAction(() => {
          if (this._userSubscription) {
            this._userSubscription.has_payment_method = true;
            this._userSubscription.payment_method_id = paymentMethodData.payment_method_id;
          }
        });
        return response;
      } else {
        notificationState.addToasterError(response.message || "Failed to update payment method");
        return { status: false, message: response.message };
      }
    } catch (e: any) {
      const error = e.message || "Error updating payment method";
      notificationState.addToasterError(error);
      return { status: false, message: error };
    }
  }

  // Check if plan change requires checkout
  requiresCheckoutForPlanChange(newPlanId: string, newPlanPrice: number): boolean {
    const currentPlan = this._userSubscription;
    
    // If no current subscription, always require checkout for paid plans
    if (!currentPlan) {
      return newPlanPrice > 0;
    }

    // If current plan is free and upgrading to paid, require checkout
    if (this.isCurrentPlanFree && newPlanPrice > 0) {
      return true;
    }

    // If user has no customer_id, require checkout
    if (!auth.currentUser?.customer_id && newPlanPrice > 0) {
      return true;
    }

    // If user has customer_id, use Customer Portal
    return false;
  }

  // Get payment method details
  async getPaymentMethodDetails() {
    try {
      const response = await apiClient.get("payment-method-details");
      return response.data;
    } catch (e: any) {
      notificationState.addToasterError("Failed to fetch payment method details");
      return null;
    }
  }
}

const subscriptionState = new SubscriptionState();
export default subscriptionState;